import { CONTENT_API } from '../config'
import { authFetch } from './authFetch'
import type { BlockEdge, BlockEdgeType } from '@arkturian/canvas-engine'

/**
 * REST client for the block_edges API (Content's Phase 4.1/4.2/4.3).
 * GET/POST/PATCH/DELETE on `/api/v1/posts/{post_id}/edges/`.
 *
 * 5 edge_types are authoritative per Spec #795 + live CHECK constraint:
 * replies_to | contradicts | extends | references | synthesizes.
 *
 * Auto-detection: any `/crdt/apply` write runs through edge_detector
 * server-side and creates edges with confidence ≥ thresholds. The
 * `edges_auto_created` count in the apply response surfaces that to
 * the UI (CanvasView toast).
 */

export interface EdgeListFilters {
  edge_type?: BlockEdgeType
  author?: string
  from_block?: string
  to_block?: string
}

export interface CreateEdgeRequest {
  from_block_id: string
  to_block_id: string
  edge_type: BlockEdgeType
  label?: string | null
  confidence?: number | null
}

export interface UpdateEdgeRequest {
  edge_type?: BlockEdgeType
  label?: string | null
  confidence?: number | null
}

const JSON_HEADERS = { 'Content-Type': 'application/json' }

export async function listEdges(
  postId: number,
  filters: EdgeListFilters = {},
): Promise<BlockEdge[]> {
  const params = new URLSearchParams()
  for (const [k, v] of Object.entries(filters)) {
    if (v) params.set(k, v)
  }
  const qs = params.toString()
  const url = `${CONTENT_API}/api/v1/posts/${postId}/edges/${qs ? `?${qs}` : ''}`
  const res = await authFetch(url, { headers: JSON_HEADERS })
  if (!res.ok) throw new Error(`listEdges: HTTP ${res.status}`)
  return res.json()
}

export async function createEdge(
  postId: number,
  body: CreateEdgeRequest,
): Promise<BlockEdge> {
  const res = await authFetch(`${CONTENT_API}/api/v1/posts/${postId}/edges/`, {
    method: 'POST',
    headers: JSON_HEADERS,
    body: JSON.stringify(body),
  })
  if (res.status === 409) {
    const data = await res.json()
    throw new EdgeConflictError(data.existing_edge_id, body)
  }
  if (!res.ok) throw new Error(`createEdge: HTTP ${res.status}`)
  return res.json()
}

export async function updateEdge(
  postId: number,
  edgeId: string,
  body: UpdateEdgeRequest,
): Promise<BlockEdge> {
  const res = await authFetch(
    `${CONTENT_API}/api/v1/posts/${postId}/edges/${edgeId}`,
    { method: 'PATCH', headers: JSON_HEADERS, body: JSON.stringify(body) },
  )
  if (!res.ok) throw new Error(`updateEdge: HTTP ${res.status}`)
  return res.json()
}

export async function deleteEdge(postId: number, edgeId: string): Promise<void> {
  const res = await authFetch(
    `${CONTENT_API}/api/v1/posts/${postId}/edges/${edgeId}`,
    { method: 'DELETE', headers: JSON_HEADERS },
  )
  if (res.status !== 204 && !res.ok) {
    throw new Error(`deleteEdge: HTTP ${res.status}`)
  }
}

/**
 * Thrown when the server rejects a create with 409 (duplicate edge).
 * Carries the existing edge_id so callers can update-or-skip.
 */
export class EdgeConflictError extends Error {
  existing_edge_id: string
  attempt: CreateEdgeRequest
  constructor(existing_edge_id: string, attempt: CreateEdgeRequest) {
    super(`Edge already exists: ${existing_edge_id}`)
    this.name = 'EdgeConflictError'
    this.existing_edge_id = existing_edge_id
    this.attempt = attempt
  }
}
